/**
 * Lettura dei messaggi "speciali" (posizione, contatto, sondaggio, chiamata).
 * Funzioni pure, testate in __tests__/messageExtras.test.ts.
 *
 * raw_payload in whatsapp_messages ha due forme: il solo payload del messaggio (core)
 * oppure l'evento intero { event, payload } (vecchia edge). Campi WAHA usati
 * (https://waha.devlike.pro/docs/how-to/receive-messages/): `location`, `vCards`,
 * `_data` del motore (NOWEB: _data.message.locationMessage / contactMessage /
 * pollCreationMessage*, WEBJS: _data.lat/_data.lng, _data.pollName/_data.pollOptions).
 */

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);

/** Il payload del messaggio, qualunque sia la forma salvata. */
export function messagePayload(raw: unknown): Obj | null {
  if (!isObj(raw)) return null;
  if (isObj(raw.payload) && (raw.event || raw.session)) return raw.payload;
  return raw;
}

function nowebMessage(p: Obj | null): Obj | null {
  const d = p && isObj(p._data) ? p._data : null;
  return d && isObj(d.message) ? d.message : null;
}

// ============ POSIZIONE ============

export interface LocationInfo { lat: number | null; lng: number | null; name?: string; address?: string }

export function parseLocation(raw: unknown, content?: string): LocationInfo | null {
  const p = messagePayload(raw);
  const loc = p && isObj(p.location) ? p.location : null;
  if (loc) {
    const lat = num(loc.latitude), lng = num(loc.longitude);
    if (lat !== null && lng !== null) {
      return { lat, lng, name: str(loc.name) ?? str(loc.description), address: str(loc.address) };
    }
  }
  const nm = nowebMessage(p);
  const lm = nm && (isObj(nm.locationMessage) ? nm.locationMessage : isObj(nm.liveLocationMessage) ? nm.liveLocationMessage : null);
  if (lm) {
    const lat = num(lm.degreesLatitude), lng = num(lm.degreesLongitude);
    if (lat !== null && lng !== null) return { lat, lng, name: str(lm.name), address: str(lm.address) };
  }
  const d = p && isObj(p._data) ? p._data : null;
  if (d) {
    const lat = num(d.lat), lng = num(d.lng);
    if (lat !== null && lng !== null) return { lat, lng, name: str(d.loc) };
  }
  // Numeri Cloud: nel DB c'e' solo "📍 <nome>" (niente coordinate).
  const text = str(content);
  if (text && !/^[A-Za-z0-9+/=]{200,}$/.test(text)) {
    const coords = text.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
    if (coords) return { lat: Number(coords[1]), lng: Number(coords[2]) };
    return { lat: null, lng: null, name: text.replace(/^(?:📍\s*|Posizione:\s*)/u, '') || undefined };
  }
  return null;
}

/**
 * Posizione dai metadata dei numeri Ufficiale: metadata.location =
 * { latitude, longitude, name, address, url } (scritto da meta-whatsapp-webhook/lib/richContent.ts).
 */
export function locationFromMetadata(metadata: unknown): LocationInfo | null {
  const loc = isObj(metadata) && isObj(metadata.location) ? metadata.location : null;
  if (!loc) return null;
  const lat = num(loc.latitude), lng = num(loc.longitude);
  if (lat === null || lng === null) return null;
  return { lat, lng, name: str(loc.name), address: str(loc.address) };
}

/** Prima i metadata (numeri Ufficiale), poi raw_payload e testo (numeri Standard). */
export function resolveLocation(metadata: unknown, raw: unknown, content?: string): LocationInfo | null {
  return locationFromMetadata(metadata) ?? parseLocation(raw, content);
}

export function mapsUrl(loc: LocationInfo): string | null {
  if (loc.lat !== null && loc.lng !== null) return `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`;
  const q = [loc.name, loc.address].filter(Boolean).join(' ');
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
}

// ============ CONTATTO (vCard) ============

export interface ContactCard { name: string; phone: string | null }

/** Legge nome (FN) e primo telefono (TEL, preferendo waid) da una vCard. */
export function parseVcard(vcard: string): ContactCard | null {
  if (!vcard || !/BEGIN:VCARD/i.test(vcard)) return null;
  const lines = vcard.replace(/\r\n[ \t]/g, '').split(/\r?\n/);
  let name = '';
  let phone: string | null = null;
  for (const line of lines) {
    const [rawKey, ...rest] = line.split(':');
    const value = rest.join(':').trim();
    const key = rawKey.toUpperCase();
    if (key === 'FN' || key.startsWith('FN;')) name = value;
    else if (!name && (key === 'N' || key.startsWith('N;'))) name = value.split(';').filter(Boolean).reverse().join(' ');
    else if (key.startsWith('TEL') || key.includes('.TEL')) {
      const waid = rawKey.match(/waid=(\d+)/i);
      const candidate = waid ? `+${waid[1]}` : value;
      if (!phone || waid) phone = candidate;
    }
  }
  if (!name && !phone) return null;
  return { name: name || phone || 'Contatto', phone };
}

export function extractContacts(raw: unknown, content?: string): ContactCard[] {
  const p = messagePayload(raw);
  const cards: string[] = [];
  if (p && Array.isArray(p.vCards)) cards.push(...p.vCards.filter((v): v is string => typeof v === 'string'));
  const nm = nowebMessage(p);
  if (nm && isObj(nm.contactMessage) && typeof nm.contactMessage.vcard === 'string') cards.push(nm.contactMessage.vcard);
  if (nm && isObj(nm.contactsArrayMessage) && Array.isArray(nm.contactsArrayMessage.contacts)) {
    for (const c of nm.contactsArrayMessage.contacts) if (isObj(c) && typeof c.vcard === 'string') cards.push(c.vcard);
  }
  if (cards.length === 0 && content && /BEGIN:VCARD/i.test(content)) cards.push(...content.split(/(?=BEGIN:VCARD)/i));
  const seen = new Set<string>();
  return cards.map(parseVcard).filter((c): c is ContactCard => {
    if (!c) return false;
    const k = `${c.name}|${c.phone}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * Contatti dai metadata dei numeri Ufficiale: metadata.contacts =
 * [{ name, phones: [{ phone, wa_id, type }], emails, company }].
 */
export function contactsFromMetadata(metadata: unknown): ContactCard[] {
  const list = isObj(metadata) && Array.isArray(metadata.contacts) ? metadata.contacts : [];
  const cards: ContactCard[] = [];
  for (const c of list) {
    if (!isObj(c)) continue;
    const phones = Array.isArray(c.phones) ? c.phones.filter(isObj) : [];
    const first = phones[0];
    const waId = first ? str(first.wa_id) : undefined;
    const phone = waId ? `+${waId.replace(/^\+/, '')}` : (first ? str(first.phone) ?? null : null);
    const name = str(c.name) ?? phone ?? '';
    if (!name && !phone) continue;
    cards.push({ name: name || 'Contatto', phone: phone ?? null });
  }
  return cards;
}

/** Prima i metadata (numeri Ufficiale), poi raw_payload e testo (numeri Standard). */
export function resolveContacts(metadata: unknown, raw: unknown, content?: string): ContactCard[] {
  const fromMeta = contactsFromMetadata(metadata);
  return fromMeta.length > 0 ? fromMeta : extractContacts(raw, content);
}

// ============ SONDAGGIO ============

export interface PollDefinition { question: string; options: string[] }

export function extractPoll(raw: unknown, content?: string): PollDefinition {
  const p = messagePayload(raw);
  const optName = (o: unknown) => (typeof o === 'string' ? o : isObj(o) ? str(o.optionName) ?? str(o.name) : undefined);
  const nm = nowebMessage(p);
  const pc = nm && (['pollCreationMessageV3', 'pollCreationMessageV2', 'pollCreationMessage']
    .map((k) => nm[k]).find(isObj) as Obj | undefined);
  if (pc) {
    return { question: str(pc.name) ?? str(content) ?? 'Sondaggio', options: (Array.isArray(pc.options) ? pc.options : []).map(optName).filter((o): o is string => !!o) };
  }
  const poll = p && isObj(p.poll) ? p.poll : null;
  if (poll) return { question: str(poll.name) ?? str(content) ?? 'Sondaggio', options: (Array.isArray(poll.options) ? poll.options : []).map(optName).filter((o): o is string => !!o) };
  const d = p && isObj(p._data) ? p._data : null;
  if (d && (d.pollName || d.pollOptions)) {
    return { question: str(d.pollName) ?? str(content) ?? 'Sondaggio', options: (Array.isArray(d.pollOptions) ? d.pollOptions : []).map(optName).filter((o): o is string => !!o) };
  }
  return { question: str(content) ?? 'Sondaggio', options: [] };
}

export interface PollTally { option: string; votes: number }

/** Conta i voti (ultimo voto per persona, come salvato dal core). Opzioni ignote in coda. */
export function tallyPoll(options: string[], votes: Array<{ selected_options: string[] | null }>): PollTally[] {
  const counts = new Map<string, number>(options.map((o) => [o, 0]));
  for (const v of votes) for (const o of v.selected_options || []) counts.set(o, (counts.get(o) || 0) + 1);
  return Array.from(counts, ([option, n]) => ({ option, votes: n }));
}

/**
 * Parti dell'id WAHA di un messaggio: la chat e l'id vero del messaggio.
 * Forme: true_<chat>_<ID>, true_<chat>_<ID>_<partecipante> (gruppi), <chat>_<ID>, <ID>.
 * Nei gruppi l'ultimo pezzo e' il numero di chi scrive, non il messaggio: prima si usava
 * quello e i voti di sondaggi diversi dello stesso autore si mescolavano.
 */
export function pollIdParts(messageId: string): { chat: string | null; id: string } {
  const parts = messageId.split('_').filter(Boolean);
  const chatIdx = parts.findIndex((p) => p.includes('@'));
  if (chatIdx >= 0 && parts[chatIdx + 1]) return { chat: parts[chatIdx], id: parts[chatIdx + 1] };
  const plain = parts.filter((p) => p !== 'true' && p !== 'false');
  return { chat: null, id: plain[0] || messageId };
}

/** Id vero del sondaggio (senza true_/false_, chat e partecipante). */
export function pollIdSuffix(messageId: string): string {
  return pollIdParts(messageId).id;
}

/**
 * Filtro LIKE su whatsapp_poll_votes.poll_message_id: solo l'id vero del sondaggio
 * (unico), cosi' si prendono solo i voti di QUESTO sondaggio (il prefisso true_/false_,
 * la chat e il partecipante finale possono esserci o no). La chat non entra nel filtro:
 * lo stesso sondaggio puo' arrivare con la chat scritta in due forme (@c.us / @lid).
 */
export function pollVotesLikePattern(messageId: string): string {
  return `%${pollIdParts(messageId).id}%`;
}

// ============ CHIAMATE ============

export interface CallLogInfo { isVideo: boolean; status: string; outgoing: boolean }

/** Legge una riga di chat di tipo 'call_log' (content JSON oppure testo libero). */
export function parseCallLog(content: string | undefined, direction?: 'inbound' | 'outbound'): CallLogInfo | string {
  const text = (content || '').trim();
  if (text.startsWith('{')) {
    try {
      const j = JSON.parse(text) as Obj;
      return {
        isVideo: j.is_video === true || j.isVideo === true,
        status: str(j.status) ?? 'received',
        outgoing: j.from_me === true || j.fromMe === true || direction === 'outbound',
      };
    } catch { /* testo libero */ }
  }
  if (text) return text;
  return { isVideo: false, status: 'received', outgoing: direction === 'outbound' };
}

/** "Chiamata vocale persa alle 14:32" ecc. */
export function formatCallLog(info: CallLogInfo, when: Date): string {
  const kind = info.isVideo ? 'Videochiamata' : 'Chiamata vocale';
  const time = when.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const s = info.status.toLowerCase();
  let esito: string;
  if (s === 'rejected') esito = info.outgoing ? 'rifiutata dal contatto' : 'rifiutata';
  else if (s === 'accepted' || s === 'answered') esito = info.outgoing ? 'effettuata' : 'ricevuta';
  else esito = info.outgoing ? 'senza risposta' : 'persa';
  return `${kind} ${esito} alle ${time}`;
}

export type CallTone = 'missed' | 'rejected' | 'normal';

export interface CallLogView { isVideo: boolean; status: string; text: string; tone: CallTone }

/**
 * Riga 'call_log' scritta dal core: metadata { kind: 'call', isVideo, call_status } con
 * call_status received | accepted | rejected | missed (inbound/handlers/call.ts).
 * null se la riga non ha questi metadata (righe vecchie: si usa parseCallLog).
 */
export function callLogFromMetadata(metadata: unknown, when: Date): CallLogView | null {
  if (!isObj(metadata) || metadata.kind !== 'call') return null;
  const isVideo = metadata.isVideo === true || metadata.is_video === true;
  const status = (str(metadata.call_status) ?? 'received').toLowerCase();
  const kind = isVideo ? 'Videochiamata' : 'Chiamata vocale';
  const esiti: Record<string, string> = { received: 'ricevuta', accepted: 'risposta', rejected: 'rifiutata', missed: 'persa' };
  const esito = esiti[status] ?? 'ricevuta';
  const time = when.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const tone: CallTone = status === 'missed' ? 'missed' : status === 'rejected' ? 'rejected' : 'normal';
  return { isVideo, status, text: `${kind} ${esito} alle ${time}`, tone };
}

// ============ ANTEPRIMA (barra messaggio fissato) ============

export function messagePreviewText(type: string, content?: string): string {
  const text = (content || '').trim();
  if (text && type !== 'location' && type !== 'contact' && !/BEGIN:VCARD/i.test(text)) return text;
  const labels: Record<string, string> = {
    image: 'Foto', video: 'Video', audio: 'Audio', ptt: 'Messaggio vocale', voice: 'Messaggio vocale',
    document: 'Documento', sticker: 'Sticker', location: 'Posizione', contact: 'Contatto', poll: 'Sondaggio',
  };
  return labels[type] || text || 'Messaggio';
}
